import { useState } from "react";
import { Copy, X } from "lucide-react";
import { useToast } from "../../components/ui/toast";
import { DELIVERY_LABELS, ownerErrorMessage } from "./access-model";
import type { InvitationsState } from "./use-invitations";

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

export function InvitationsList({ state }: { state: InvitationsState }) {
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied");
    } catch {
      toast.danger("Copy failed", link);
    }
  };

  const act = async (id: string, verb: "resend" | "revoke") => {
    if (verb === "revoke" && !window.confirm("Revoke this invitation? Its link stops working.")) return;
    setBusyId(id);
    try {
      if (verb === "resend") {
        await state.resend(id);
        toast.success("Invitation resent", "The previous link no longer works.");
      } else {
        await state.revoke(id);
        toast.success("Invitation revoked");
      }
    } catch (error) {
      toast.danger(verb === "resend" ? "Resend failed" : "Revoke failed", ownerErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      {state.lastIssued && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand/30 bg-brand-soft p-4 text-sm">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-ink">Invitation created for {state.lastIssued.invite.email}</p>
            <p className="text-xs text-ink-2">
              {state.lastIssued.link
                ? "Copy the link now: it is shown only once. Resending issues a new one."
                : "No link was returned for this invitation."}
            </p>
          </div>
          {state.lastIssued.link && (
            <button
              onClick={() => { void copy(state.lastIssued!.link!); }}
              className="flex items-center gap-2 rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
            >
              <Copy className="h-4 w-4" /> Copy invite link
            </button>
          )}
          <button aria-label="Dismiss" onClick={state.dismissIssued} className="rounded-lg p-2 text-ink-3 hover:bg-soft">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-line bg-elevated shadow-sm">
        <h4 className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">Invitations</h4>
        {state.invitations.length === 0 ? (
          <p className="px-4 py-6 text-sm text-ink-3">{state.loading ? "Loading…" : "No invitations yet."}</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-line text-xs text-ink-3">
                <th className="px-4 py-2 font-semibold">Email</th>
                <th className="px-4 py-2 font-semibold">Role</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">Delivery</th>
                <th className="px-4 py-2 font-semibold">Expires</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {state.invitations.map((inv) => {
                const pending = inv.status === "pending";
                const link = state.links[inv.id];
                return (
                  <tr key={inv.id}>
                    <td className="px-4 py-2 font-mono text-xs text-ink">{inv.email}</td>
                    <td className="px-4 py-2 capitalize text-ink-2">{inv.role_id}</td>
                    <td className="px-4 py-2 capitalize text-ink-2">{inv.status}</td>
                    <td className="px-4 py-2 text-ink-2">
                      {DELIVERY_LABELS[String(inv.delivery?.state)] ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-ink-2">{formatDate(inv.expires_at)}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-2">
                        {link && pending && (
                          <button onClick={() => { void copy(link); }} className="text-xs font-semibold text-brand hover:underline">
                            Copy link
                          </button>
                        )}
                        {pending && (
                          <>
                            <button
                              disabled={busyId === inv.id}
                              onClick={() => { void act(inv.id, "resend"); }}
                              className="text-xs font-semibold text-brand hover:underline disabled:opacity-50"
                            >
                              Resend
                            </button>
                            <button
                              disabled={busyId === inv.id}
                              onClick={() => { void act(inv.id, "revoke"); }}
                              className="text-xs font-semibold text-danger hover:underline disabled:opacity-50"
                            >
                              Revoke
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
